import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { FORMA_LINK_BTN } from '../../utils/listPageUi';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
  PieChart, Pie, Cell
} from 'recharts';
import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  BanknotesIcon,
  PresentationChartBarIcon,
  DocumentTextIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
// PDF Export libraries
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
// Removed API imports - using only real-time localStorage data
import localStorageService from '../../utils/localStorageService';
import toast from 'react-hot-toast';

const ReportsPage = () => {
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState('month');
  const [reportType, setReportType] = useState('financial');
  const [isExporting, setIsExporting] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [viewMode, setViewMode] = useState('chart'); // 'chart' or 'table'

  // Reset view mode to chart when report type changes
  useEffect(() => {
    setViewMode('chart');
  }, [reportType]);

  // Force data refresh when date range changes
  useEffect(() => {
    console.log('📅 Date range changed to:', dateRange);
    // Add a small delay to ensure state is updated
    setTimeout(() => {
      queryClient.invalidateQueries(['financialReport']);
      queryClient.invalidateQueries(['studentsReport']);
      queryClient.invalidateQueries(['salesReport']);
    }, 100);
  }, [dateRange, customStartDate, customEndDate, queryClient]);
  
  // Set default date range based on current date
  useEffect(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setCustomStartDate(firstDayOfMonth.toISOString().split('T')[0]);
    setCustomEndDate(lastDayOfMonth.toISOString().split('T')[0]);
  }, []);
  
  // Get date parameters based on selected range
  const getDateParams = () => {
    const now = new Date();
    let startDate, endDate;
    
    console.log('📅 getDateParams called with dateRange:', dateRange, 'customStartDate:', customStartDate, 'customEndDate:', customEndDate);
    
    switch (dateRange) {
      case 'week':
        // Current week
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
        startDate = new Date(now.setDate(diff));
        endDate = new Date(now);
        endDate.setDate(startDate.getDate() + 6);
        break;
      case 'month':
        // Current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'quarter':
        // Current quarter
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), (quarter + 1) * 3, 0);
        break;
      case 'year':
        // Current year
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        // Custom date range
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          // Fallback to current month if custom dates are not set
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }
        break;
      default:
        // Default to current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    
    const result = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
    
    console.log('📅 getDateParams result:', result);
    return result;
  };

  // Fetch organization settings for PDF header
  const { data: organizationSettings } = useQuery('organizationSettings', () => {
    try {
      const localSettings = localStorage.getItem('organizationSettings');
      return localSettings ? JSON.parse(localSettings) : {
        gymName: 'FORMAPRO',
        address: 'École de Formation',
        phone: 'Téléphone non renseigné',
        email: 'contact@formapro.com',
        logo: null
      };
    } catch (error) {
      console.error('Error fetching organization settings:', error);
      return {
        gymName: 'FORMAPRO',
        address: 'École de Formation',
        phone: 'Téléphone non renseigné',
        email: 'contact@formapro.com',
        logo: null
      };
    }
  }, {
    staleTime: 30000 // 30 seconds
  });

  // Query for financial data - connected to payments cache
  const { data: financialData, isLoading: isFinancialLoading, refetch: refetchFinancial } = useQuery(
    ['financialReport', 'payments', reportType, dateRange, customStartDate, customEndDate],
    () => {
      // Always generate financial data for real-time reports
      
      try {
      const { startDate, endDate } = getDateParams();
        console.log('📊 Reports: Generating real-time financial data...');
        
        // Get REAL data from localStorage (same method as PaymentsPage)
        const payments = localStorageService.getAllItems('payments') || [];
        const expenses = JSON.parse(localStorage.getItem('expenses') || '[]');
        const students = localStorageService.getAllItems('students') || [];
        const courses = localStorageService.getAllItems('courses') || [];
      
      console.log('📊 Reports Data loaded:', {
        payments: payments.length,
        expenses: expenses.length,
        students: students.length,
        courses: courses.length,
        samplePayment: payments[0],
        recentPayments: payments.slice(-5),
        allPayments: payments
      });
      
      // FORCE DEBUG: Check if we have ANY payments at all
      if (payments.length === 0) {
        console.error('🚨 NO PAYMENTS FOUND IN LOCALSTORAGE!');
        console.log('🔍 Checking localStorage directly:', localStorage.getItem('payments'));
      } else {
        console.log('✅ Found payments, analyzing...');
        payments.forEach((payment, index) => {
          console.log(`Payment ${index + 1}:`, {
            id: payment.id,
            date: payment.date,
            amount: payment.amount,
            paidAmount: payment.paidAmount,
            status: payment.status,
            isPaid: payment.isPaid
          });
        });
      }

      // Calculate financial data based on selected period
      const timeSeries = [];
      const monthNames = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
      const shortMonthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      
      console.log(`📊 Generating financial chart for period: ${dateRange}`);
      console.log(`📊 Using date range: ${startDate} to ${endDate}`);
      
      // Filter payments to the selected date range FIRST
      const rangeStartDate = new Date(startDate);
      const rangeEndDate = new Date(endDate);
      rangeEndDate.setHours(23, 59, 59, 999); // Include the entire end date
      
      const filteredPayments = payments.filter(payment => {
        const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
        return paymentDate >= rangeStartDate && paymentDate <= rangeEndDate && !isNaN(paymentDate.getTime());
      });
      
      console.log(`📊 Filtered ${filteredPayments.length} payments from ${payments.length} total payments for range ${startDate} to ${endDate}`);
      
      if (dateRange === 'week') {
        // For weekly view, show daily data for the week
        const weekStartDate = new Date(startDate);
        const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        
        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(weekStartDate);
          dayDate.setDate(weekStartDate.getDate() + i);
          const nextDayDate = new Date(dayDate);
          nextDayDate.setDate(dayDate.getDate() + 1);
          
          const monthStr = `${dayNames[dayDate.getDay()]} ${dayDate.getDate()}`;
          
          // Filter payments for this day from already filtered payments
          const monthlyPayments = filteredPayments.filter(payment => {
            const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
            return paymentDate >= dayDate && paymentDate < nextDayDate;
          });
          
          // Calculate daily income and expenses
          const monthlyIncome = monthlyPayments.reduce((sum, payment) => {
            const isPaidStatus = payment.isPaid || payment.status === 'paid' || payment.status === 'partial';
            const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
            
            if (isPaidStatus) {
              return sum + (isNaN(paidAmount) ? 0 : paidAmount);
            }
            return sum;
          }, 0);
          
          const teacherCosts = monthlyIncome * 0.4; // 40% commission
          const educationalExpenses = 0; // No expenses for daily view
          const totalMonthlyExpenses = teacherCosts + educationalExpenses;
          const monthlyProfit = monthlyIncome - totalMonthlyExpenses;
          
          if (monthlyIncome > 0) {
            console.log(`💰 Day ${monthStr}: ${monthlyIncome.toLocaleString()} DA revenue`);
          }
          
          timeSeries.push({
            period: monthStr,
            income: Math.round(monthlyIncome),
            expenses: Math.round(totalMonthlyExpenses),
            profit: Math.round(monthlyProfit)
          });
        }
      } else if (dateRange === 'month') {
        // For monthly view, show weekly data for the month
        const monthStartDate = new Date(startDate);
        const monthEndDate = new Date(endDate);
        
        let weekStart = new Date(monthStartDate);
        let weekNumber = 1;
        
        while (weekStart <= monthEndDate && weekNumber <= 5) {
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          if (weekEnd > monthEndDate) weekEnd.setTime(monthEndDate.getTime());
          
          const monthStr = `Sem ${weekNumber}`;
          
          // Filter payments for this week
          const monthlyPayments = filteredPayments.filter(payment => {
            const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
            return paymentDate >= weekStart && paymentDate <= weekEnd && !isNaN(paymentDate.getTime());
          });
          
          // Calculate weekly income and expenses
          const monthlyIncome = monthlyPayments.reduce((sum, payment) => {
            const isPaidStatus = payment.isPaid || payment.status === 'paid' || payment.status === 'partial';
            const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
            
            if (isPaidStatus) {
              return sum + (isNaN(paidAmount) ? 0 : paidAmount);
            }
            return sum;
          }, 0);
          
          const teacherCosts = monthlyIncome * 0.4; // 40% commission
          const educationalExpenses = 0; // Simplified for weekly view
          const totalMonthlyExpenses = teacherCosts + educationalExpenses;
          const monthlyProfit = monthlyIncome - totalMonthlyExpenses;
          
          if (monthlyIncome > 0) {
            console.log(`💰 Week ${weekNumber}: ${monthlyIncome.toLocaleString()} DA revenue`);
          }
          
          timeSeries.push({
            period: monthStr,
            income: Math.round(monthlyIncome),
            expenses: Math.round(totalMonthlyExpenses),
            profit: Math.round(monthlyProfit)
          });
          
          weekStart.setDate(weekStart.getDate() + 7);
          weekNumber++;
        }
      } else {
        // For quarter/year view or default, show monthly data
        // Use the actual date range instead of assuming current date
        const actualStartDate = new Date(startDate);
        const actualEndDate = new Date(endDate);
        const currentDate = new Date();
        const monthsToShow = dateRange === 'quarter' ? 3 : 6;
        
        for (let i = monthsToShow - 1; i >= 0; i--) {
          const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
          const nextMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i + 1, 1);
          const year = monthDate.getFullYear();
          const month = monthDate.getMonth();
          const monthStr = `${year}-${monthNames[month]}`;
          
          // Filter payments for this month
          const monthlyPayments = filteredPayments.filter(payment => {
            const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
            const isInMonth = paymentDate >= monthDate && paymentDate < nextMonthDate && !isNaN(paymentDate.getTime());
            
            // Debug only when we find payments in current month
            if (monthStr === '2025-09' && isInMonth) {
              console.log(`📊 Found payment for ${monthStr}: ${payment.paidAmount || payment.amount} DA`);
            }
            
            return isInMonth;
          });
        
        // Filter expenses for this month  
        const monthlyExpenses = expenses.filter(expense => {
          const expenseDate = new Date(expense.date || expense.createdAt);
          return expenseDate >= monthDate && expenseDate < nextMonthDate && !isNaN(expenseDate.getTime());
        });
        
        // Calculate monthly income (only from PAID payments)
        const monthlyIncome = monthlyPayments.reduce((sum, payment) => {
          const isPaidStatus = payment.isPaid || payment.status === 'paid' || payment.status === 'partial';
          const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
          
          if (monthStr === '2025-09' && isPaidStatus && paidAmount > 0) {
            console.log(`💰 Processing payment income for ${monthStr}: ${paidAmount} DA`);
          }
          
          if (isPaidStatus) {
            return sum + (isNaN(paidAmount) ? 0 : paidAmount);
          }
          return sum;
        }, 0);
        
        // Calculate monthly teacher costs (40% commission)
        const teacherCosts = monthlyPayments.reduce((total, payment) => {
          if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
            const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
            const commission = paidAmount * 0.4; // 40% commission
            return total + (isNaN(commission) ? 0 : commission);
          }
          return total;
        }, 0);
        
        // Calculate monthly educational expenses
        const educationalExpenses = monthlyExpenses.reduce((total, expense) => {
          const amount = parseFloat(expense.amount || 0);
          return total + (isNaN(amount) ? 0 : amount);
        }, 0);
        
        const totalMonthlyExpenses = teacherCosts + educationalExpenses;
        const monthlyProfit = monthlyIncome - totalMonthlyExpenses;
        
        if (monthlyIncome > 0) {
          console.log(`💰 Month ${monthStr}: ${monthlyIncome.toLocaleString()} DA revenue, ${monthlyPayments.length} payments`);
        }
        
        timeSeries.push({
          period: monthStr,
          income: Math.round(monthlyIncome),
          expenses: Math.round(totalMonthlyExpenses),
          profit: Math.round(monthlyProfit)
        });
        }
      }
      
      // Log final timeSeries data
      console.log('📊 Final timeSeries with REAL payment data:', timeSeries);
      
      // Calculate overall summary for the selected date range
      const { startDate: rangeStart, endDate: rangeEnd } = getDateParams();
      const summaryStartDate = new Date(rangeStart);
      const summaryEndDate = new Date(rangeEnd);
      
      // Filter data for selected date range
      const rangePayments = filteredPayments.filter(payment => {
        const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
        return paymentDate >= summaryStartDate && paymentDate <= summaryEndDate && !isNaN(paymentDate.getTime());
      });
      
      const rangeExpenses = expenses.filter(expense => {
        const expenseDate = new Date(expense.date || expense.createdAt);
        return expenseDate >= summaryStartDate && expenseDate <= summaryEndDate && !isNaN(expenseDate.getTime());
      });
      
      // Calculate totals for the range
      const totalIncome = rangePayments.reduce((sum, payment) => {
        if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
          const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
          return sum + (isNaN(paidAmount) ? 0 : paidAmount);
        }
        return sum;
      }, 0);
      
      const totalTeacherCosts = rangePayments.reduce((total, payment) => {
        if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
          const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
          const commission = paidAmount * 0.4;
          return total + (isNaN(commission) ? 0 : commission);
        }
        return total;
      }, 0);
      
      const totalEducationalExpenses = rangeExpenses.reduce((total, expense) => {
        const amount = parseFloat(expense.amount || 0);
        return total + (isNaN(amount) ? 0 : amount);
      }, 0);
      
      const totalExpenses = totalTeacherCosts + totalEducationalExpenses;
      const netProfit = totalIncome - totalExpenses;
      const profitMargin = totalIncome > 0 ? (netProfit / totalIncome) * 100 : 0;
      
      // Income breakdown by type
      const incomeByType = rangePayments.reduce((acc, payment) => {
        if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
          const type = payment.type || 'course';
          const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
          acc[type] = (acc[type] || 0) + (isNaN(paidAmount) ? 0 : paidAmount);
        }
        return acc;
      }, {});
      
      // Expenses breakdown by type
      let expensesByType = {};
      try {
        expensesByType = rangeExpenses.reduce((acc, expense) => {
          const category = expense.category || 'other';
          const amount = parseFloat(expense.amount || 0);
          acc[category] = (acc[category] || 0) + (isNaN(amount) ? 0 : amount);
          return acc;
        }, {});
        
        // Add teacher costs to expenses breakdown
        expensesByType['teacher_commissions'] = totalTeacherCosts;
      } catch (error) {
        console.error('Error calculating expenses breakdown:', error);
        expensesByType = { teacher_commissions: totalTeacherCosts };
      }
      
      console.log('📊 Reports: Financial summary calculated:', {
        totalIncome: totalIncome.toLocaleString() + ' DA',
        totalExpenses: totalExpenses.toLocaleString() + ' DA',
        netProfit: netProfit.toLocaleString() + ' DA',
        profitMargin: profitMargin.toFixed(1) + '%',
        timeSeriesLength: timeSeries.length
      });
      
        return {
          report: {
            data: {
              summary: {
              totalIncome: Math.round(totalIncome),
              totalExpenses: Math.round(totalExpenses),
              netProfit: Math.round(netProfit),
              profitMargin: Math.round(profitMargin * 100) / 100
            },
            timeSeries,
              benefits: {
              incomeByType,
              expensesByType
            }
          }
        }
      };
      } catch (error) {
        console.error('🚨 Error calculating financial data:', error);
        console.log('📊 Falling back to simple data structure...');
        
        // Return simple fallback data structure
        return {
          report: {
            data: {
              summary: {
                totalIncome: 0,
                totalExpenses: 0,
                netProfit: 0,
                profitMargin: 0
              },
              timeSeries: [],
              benefits: {
                incomeByType: {},
                expensesByType: {}
              }
            }
          }
        };
      }
    },
    {
      enabled: true,
      staleTime: 0, // Always fetch fresh data like PaymentsPage
      cacheTime: 1000, // Short cache like PaymentsPage
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnMount: true, // Always refetch on mount
      retry: false,
      onError: (error) => {
        console.error('Error fetching financial report:', error);
      }
    }
  );
  
  // Query for students data
  const { data: studentsData, isLoading: isStudentsLoading, refetch: refetchStudents } = useQuery(
    ['studentsReport', reportType, dateRange, customStartDate, customEndDate],
    async () => {
      // Always generate students data for real-time reports
      
      const { startDate, endDate } = getDateParams();
      console.log('📊 Reports: Generating real-time students data...');
      
      // Get real data from localStorage
      const students = localStorageService.getAllItems('students') || [];
      const payments = localStorageService.getAllItems('payments') || [];
      
      console.log('📊 Students Data loaded:', {
        students: students.length,
        payments: payments.length
      });
      
      // Calculate current month stats
      const currentDate = new Date();
      const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      
      // Get new students this month (based on enrollment date)
      const newThisMonth = students.filter(student => {
        const enrollmentDate = new Date(student.enrollmentDate || student.createdAt);
        return enrollmentDate >= currentMonthStart && !isNaN(enrollmentDate.getTime());
      }).length;
      
      // Calculate student status
      const activeStudents = students.filter(s => s.status === 'active' || s.currentCourse).length;
      const inactiveStudents = students.filter(s => s.status === 'inactive' || (!s.currentCourse && s.status !== 'active')).length;
      
      // Generate real monthly trend data (last 6 months)
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      const monthlyData = [];
      
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const nextMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i + 1, 1);
        const monthName = monthNames[monthDate.getMonth()];
        
        // Students enrolled in this month
        const monthlyNewStudents = students.filter(student => {
          const enrollmentDate = new Date(student.enrollmentDate || student.createdAt);
          return enrollmentDate >= monthDate && enrollmentDate < nextMonthDate && !isNaN(enrollmentDate.getTime());
        }).length;
        
        // Students who made payments in this month (active indicator)
        const studentsWithPayments = new Set();
        payments.forEach(payment => {
          const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
          if (paymentDate >= monthDate && paymentDate < nextMonthDate && !isNaN(paymentDate.getTime())) {
            studentsWithPayments.add(payment.studentId || payment.memberId);
          }
        });
        
        const monthlyActiveStudents = studentsWithPayments.size;
        
        // Estimate inactive students (total minus active)
        const monthlyTotalStudents = students.filter(student => {
          const enrollmentDate = new Date(student.enrollmentDate || student.createdAt);
          return enrollmentDate <= nextMonthDate && !isNaN(enrollmentDate.getTime());
        }).length;
        
        const monthlyInactiveStudents = Math.max(0, monthlyTotalStudents - monthlyActiveStudents);
        
        monthlyData.push({
          month: monthName,
          nouveaux: monthlyNewStudents,
          actifs: monthlyActiveStudents,
          inactifs: monthlyInactiveStudents
        });
      }
      
      console.log('📊 Students Report calculated:', {
        total: students.length,
        active: activeStudents,
        inactive: inactiveStudents,
        newThisMonth,
        monthlyDataPoints: monthlyData.length
      });
      
      return {
        summary: {
          total: students.length,
          active: activeStudents,
          inactive: inactiveStudents,
          newThisMonth
        },
        monthlyData
      };
    },
    {
      enabled: true, // Always enabled for real-time data
      staleTime: 0, // Always fetch fresh data
      cacheTime: 0, // No cache to force fresh calculations
      refetchInterval: false, // Disable auto-refresh
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      onError: (error) => {
        console.error('Error fetching students report:', error);
      }
    }
  );
  
  // Query for courses data
  const { data: coursesData, isLoading: isCoursesLoading, refetch: refetchCourses } = useQuery(
    ['coursesReport', reportType],
    async () => {
      // Always generate courses data for real-time reports
      
      console.log('📊 Reports: Generating real-time courses data...');
      
      // Get real data from localStorage
      const courses = localStorageService.getAllItems('courses') || [];
      const students = localStorageService.getAllItems('students') || [];
      const payments = localStorageService.getAllItems('payments') || [];
      const sessions = localStorageService.getAllItems('training-sessions') || [];
      
      console.log('📊 Courses Data loaded:', {
        courses: courses.length,
        students: students.length,
        payments: payments.length,
        sessions: sessions.length
      });
      
      // Calculate analytics for each individual course
      const courseAnalytics = {};
      
      courses.forEach(course => {
        // Calculate analytics for each course
        const courseStudents = students.filter(s => s.currentCourse === course.id || s.courseId === course.id);
        const coursePayments = payments.filter(p => p.courseId === course.id);
        const courseSessions = sessions.filter(s => s.courseId === course.id);
        
        // Calculate revenue for this course
        const courseRevenue = coursePayments.reduce((sum, payment) => {
          if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
            const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
            return sum + (isNaN(paidAmount) ? 0 : paidAmount);
          }
          return sum;
        }, 0);
        
        // Calculate completion rate (students who completed vs enrolled)
        const completedStudents = courseStudents.filter(s => s.status === 'completed' || s.courseStatus === 'completed').length;
        const completionRate = courseStudents.length > 0 ? (completedStudents / courseStudents.length) * 100 : 0;
        
        courseAnalytics[course.id] = {
          id: course.id,
          name: course.name || course.title || `Course ${course.id}`,
          category: course.category || 'other',
          enrolledStudents: courseStudents.length,
          completedStudents,
          completionRate,
          revenue: courseRevenue,
          sessionsCount: courseSessions.length,
          avgRevenuePerStudent: courseStudents.length > 0 ? courseRevenue / courseStudents.length : 0
        };
      });
      
      // Colors for different courses
      const courseColors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'];
      
      // Create pie chart data showing individual courses by enrollment
      const pieData = Object.values(courseAnalytics)
        .filter(course => course.enrolledStudents > 0) // Only show courses with students
        .map((course, index) => ({
          name: course.name,
          value: course.enrolledStudents,
          color: courseColors[index % courseColors.length],
          revenue: course.revenue
        }));
      
      // If no courses have students, show all courses with equal distribution
      if (pieData.length === 0 && courses.length > 0) {
        courses.forEach((course, index) => {
          pieData.push({
            name: course.name || course.title || `Course ${course.id}`,
            value: 1, // Equal distribution
            color: courseColors[index % courseColors.length],
            revenue: 0
          });
        });
      }
      
      console.log('📊 Course Pie Chart Data:', pieData);
      
      // Calculate top performing courses
      const topCourses = Object.values(courseAnalytics)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      
      // Calculate overall statistics
      const totalRevenue = Object.values(courseAnalytics).reduce((sum, course) => sum + course.revenue, 0);
      const totalEnrollments = Object.values(courseAnalytics).reduce((sum, course) => sum + course.enrolledStudents, 0);
      const avgCompletionRate = courses.length > 0 ? 
        Object.values(courseAnalytics).reduce((sum, course) => sum + course.completionRate, 0) / courses.length : 0;
      
      console.log('📊 Courses Report calculated:', {
        totalCourses: courses.length,
        activeCourses: courses.filter(c => c.status === 'active' || !c.status).length,
        totalRevenue: totalRevenue.toLocaleString() + ' DA',
        totalEnrollments,
        avgCompletionRate: avgCompletionRate.toFixed(1) + '%'
      });
      
      return {
        distribution: pieData,
        totalCourses: courses.length,
        activeCourses: courses.filter(c => c.status === 'active' || !c.status).length,
        analytics: {
          totalRevenue,
          totalEnrollments, 
          avgCompletionRate,
          topCourses,
          courseDetails: Object.values(courseAnalytics)
        }
      };
    },
    {
      enabled: true, // Always enabled for real-time data
      staleTime: 0, // Always fetch fresh data
      cacheTime: 0, // No cache to force fresh calculations
      refetchInterval: false, // Disable auto-refresh
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      onError: (error) => {
        console.error('Error fetching courses report:', error);
      }
    }
  );
  
  // Query for sales data
  const { data: salesData, isLoading: isSalesLoading, refetch: refetchSales } = useQuery(
    ['salesReport', reportType, dateRange, customStartDate, customEndDate],
    async () => {
      // Always generate sales data for real-time reports
      
      const { startDate, endDate } = getDateParams();
      console.log('📊 Reports: Generating real-time sales data...');
      
      // Get real data from localStorage
      const payments = localStorageService.getAllItems('payments') || [];
      const students = localStorageService.getAllItems('students') || [];
      const courses = localStorageService.getAllItems('courses') || [];
      
      console.log('📊 Sales Data loaded:', {
        payments: payments.length,
        students: students.length,
        courses: courses.length,
        dateRange: dateRange,
        startDate: startDate,
        endDate: endDate
      });
      
      // Filter payments for selected date range
      const rangeStartDate = new Date(startDate);
      const rangeEndDate = new Date(endDate);
      
      const rangePayments = payments.filter(payment => {
        const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
        return paymentDate >= rangeStartDate && paymentDate <= rangeEndDate && !isNaN(paymentDate.getTime());
      });
      
      // Calculate sales by payment method
      const salesByMethod = rangePayments.reduce((acc, payment) => {
        if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
          const method = payment.paymentMethod || payment.method || 'cash';
          const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
          acc[method] = (acc[method] || 0) + (isNaN(paidAmount) ? 0 : paidAmount);
        }
        return acc;
      }, {});
      
      // Calculate sales by course
      const salesByCourse = {};
      rangePayments.forEach(payment => {
        if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
          const course = courses.find(c => c.id === payment.courseId);
          const courseName = course ? (course.name || course.title) : 'Cours non spécifié';
          const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
          salesByCourse[courseName] = (salesByCourse[courseName] || 0) + (isNaN(paidAmount) ? 0 : paidAmount);
        }
      });
      
      // Calculate sales trends based on selected period
      let monthlySales = [];
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      
      console.log(`📊 Generating sales chart for period: ${dateRange}`);
      
      if (dateRange === 'week') {
        // For weekly view, show daily sales for the week
        const weekStartDate = new Date(rangeStartDate);
        const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        
        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(weekStartDate);
          dayDate.setDate(weekStartDate.getDate() + i);
          const nextDayDate = new Date(dayDate);
          nextDayDate.setDate(dayDate.getDate() + 1);
          
          const dayPayments = rangePayments.filter(payment => {
            const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
            return paymentDate >= dayDate && paymentDate < nextDayDate;
          });
          
          const dailySalesAmount = dayPayments.reduce((sum, payment) => {
            if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
              const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
              return sum + (isNaN(paidAmount) ? 0 : paidAmount);
            }
            return sum;
          }, 0);
          
          monthlySales.push({
            month: `${dayNames[dayDate.getDay()]} ${dayDate.getDate()}`,
            sales: Math.round(dailySalesAmount),
            transactions: dayPayments.filter(p => p.isPaid || p.status === 'paid' || p.status === 'partial').length,
            avgPerTransaction: dayPayments.length > 0 ? Math.round(dailySalesAmount / dayPayments.length) : 0
          });
        }
      } else if (dateRange === 'month') {
        // For monthly view, show weekly sales for the month
        const monthStartDate = new Date(rangeStartDate);
        const monthEndDate = new Date(rangeEndDate);
        
        let weekStart = new Date(monthStartDate);
        let weekNumber = 1;
        
        while (weekStart <= monthEndDate && weekNumber <= 5) {
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          if (weekEnd > monthEndDate) weekEnd.setTime(monthEndDate.getTime());
          
          const weekPayments = rangePayments.filter(payment => {
            const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
            return paymentDate >= weekStart && paymentDate <= weekEnd;
          });
          
          const weeklySalesAmount = weekPayments.reduce((sum, payment) => {
            if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
              const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
              return sum + (isNaN(paidAmount) ? 0 : paidAmount);
            }
            return sum;
          }, 0);
          
          monthlySales.push({
            month: `Sem ${weekNumber}`,
            sales: Math.round(weeklySalesAmount),
            transactions: weekPayments.filter(p => p.isPaid || p.status === 'paid' || p.status === 'partial').length,
            avgPerTransaction: weekPayments.length > 0 ? Math.round(weeklySalesAmount / weekPayments.length) : 0
          });
          
          weekStart.setDate(weekStart.getDate() + 7);
          weekNumber++;
        }
      } else {
        // For quarter/year view or default, show recent months
        const currentDate = new Date();
        const monthsToShow = dateRange === 'quarter' ? 3 : 6;
        
        for (let i = monthsToShow - 1; i >= 0; i--) {
          const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
          const nextMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i + 1, 1);
          const monthName = monthNames[monthDate.getMonth()];
          
          const monthlyPayments = payments.filter(payment => {
            const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
            return paymentDate >= monthDate && paymentDate < nextMonthDate && !isNaN(paymentDate.getTime());
          });
          
          const monthlySalesAmount = monthlyPayments.reduce((sum, payment) => {
            if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
              const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
              return sum + (isNaN(paidAmount) ? 0 : paidAmount);
            }
            return sum;
          }, 0);
          
          const monthlyTransactions = monthlyPayments.filter(p => p.isPaid || p.status === 'paid' || p.status === 'partial').length;
          
          monthlySales.push({
            month: monthName,
            sales: Math.round(monthlySalesAmount),
            transactions: monthlyTransactions,
            avgPerTransaction: monthlyTransactions > 0 ? Math.round(monthlySalesAmount / monthlyTransactions) : 0
          });
        }
      }
      
      // Calculate summary statistics
      const totalSales = Object.values(salesByMethod).reduce((sum, amount) => sum + amount, 0);
      const totalTransactions = rangePayments.filter(p => p.isPaid || p.status === 'paid' || p.status === 'partial').length;
      const avgTransactionValue = totalTransactions > 0 ? totalSales / totalTransactions : 0;
      
      // Top payment methods
      const paymentMethodsData = Object.entries(salesByMethod).map(([method, amount]) => ({
        name: method === 'cash' ? 'Espèces' : method === 'card' ? 'Carte' : method === 'transfer' ? 'Virement' : method,
        value: Math.round(amount),
        percentage: totalSales > 0 ? ((amount / totalSales) * 100).toFixed(1) : 0
      })).sort((a, b) => b.value - a.value);
      
      // Top courses by revenue
      const topCourses = Object.entries(salesByCourse)
        .map(([course, revenue]) => ({
          name: course,
          revenue: Math.round(revenue)
        }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);
      
      console.log('📊 Sales Report calculated:', {
        totalSales: totalSales.toLocaleString() + ' DA',
        totalTransactions,
        avgTransactionValue: avgTransactionValue.toFixed(0) + ' DA',
        topPaymentMethod: paymentMethodsData[0]?.name || 'Aucun',
        monthlySalesPoints: monthlySales.length
      });
      
      return {
        summary: {
          totalSales: Math.round(totalSales),
          totalTransactions,
          avgTransactionValue: Math.round(avgTransactionValue),
          period: `${startDate} à ${endDate}`
        },
        salesByMethod: paymentMethodsData,
        salesByCourse: topCourses,
        monthlySales,
        trends: {
          salesGrowth: monthlySales.length >= 2 ? 
            ((monthlySales[monthlySales.length - 1].sales - monthlySales[monthlySales.length - 2].sales) / 
            monthlySales[monthlySales.length - 2].sales * 100).toFixed(1) : 0
        }
      };
    },
    {
      enabled: true, // Always enabled for real-time data
      staleTime: 0, // Always fetch fresh data
      cacheTime: 0, // No cache to force fresh calculations
      refetchInterval: false, // Disable auto-refresh
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      onError: (error) => {
        console.error('Error fetching sales report:', error);
      }
    }
  );
  
  // Generate dashboard stats from real-time localStorage data
  const { data: dashboardData, isLoading: isDashboardLoading, refetch: refetchDashboard } = useQuery(
    'dashboardOverview',
    () => {
      console.log('📊 Reports: Generating real-time dashboard stats...');
      
      // Get real data from localStorage
      const payments = localStorageService.getAllItems('payments') || [];
      const students = localStorageService.getAllItems('students') || [];
      const courses = localStorageService.getAllItems('courses') || [];
      const expenses = JSON.parse(localStorage.getItem('expenses') || '[]');
      
      // Calculate current month totals
      const currentDate = new Date();
      const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const currentMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const currentMonthPayments = payments.filter(payment => {
        const paymentDate = new Date(payment.date || payment.createdAt || payment.paymentDate);
        return paymentDate >= currentMonthStart && paymentDate <= currentMonthEnd && !isNaN(paymentDate.getTime());
      });
      
      const currentMonthRevenue = currentMonthPayments.reduce((sum, payment) => {
        if (payment.isPaid || payment.status === 'paid' || payment.status === 'partial') {
          const paidAmount = parseFloat(payment.paidAmount || payment.amount || 0);
          return sum + (isNaN(paidAmount) ? 0 : paidAmount);
        }
        return sum;
      }, 0);
      
      const activeStudents = students.filter(s => s.status === 'active' || s.currentCourse).length;
      const activeCourses = courses.filter(c => c.status === 'active' || !c.status).length;
      
      return {
        data: {
          totalStudents: students.length,
          activeStudents,
          totalCourses: courses.length,
          activeCourses,
          monthlyRevenue: Math.round(currentMonthRevenue),
          totalPayments: payments.length,
          totalExpenses: expenses.length
        }
      };
    },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: (error) => {
        console.error('Error calculating dashboard stats:', error);
      }
    }
  );
  

  // Format pour la monnaie (DA)
  const formatCurrency = (value) => {
    return `${value.toLocaleString('fr-FR')} DA`;
  };

  // Render financial table view
  const renderFinancialTable = (data) => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center text-slate-600 dark:text-slate-400">
            <p className="text-lg mb-2">📊 Aucune donnée de revenus</p>
            <p className="text-sm mb-2">Allez à la page <strong>Paiements</strong> pour ajouter des transactions</p>
            <p className="text-xs">Les données apparaîtront automatiquement ici une fois les paiements ajoutés</p>
          </div>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto h-96">
        <table className="min-w-full">
          <thead>
            <tr className="bg-slate-100/80 dark:bg-slate-800/40">
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Période
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Revenus
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Dépenses
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Bénéfices
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Marge
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {data.map((item, index) => {
              const profit = item.revenue - item.expenses;
              const margin = item.revenue > 0 ? ((profit / item.revenue) * 100) : 0;
              
              return (
                <tr key={index} className="hover:bg-slate-100/80 dark:bg-slate-800/40 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-50">
                    {item.month}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className="text-forma-700 dark:text-forma-300 font-medium">
                      {formatCurrency(item.revenue)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className="text-red-400 font-medium">
                      {formatCurrency(item.expenses)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className={`font-medium ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(profit)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <span className={`font-medium ${margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {margin.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const isLoading = isFinancialLoading || isStudentsLoading || isCoursesLoading || isSalesLoading || isDashboardLoading;

  // Map data for charts with debugging
  const financialChartData = React.useMemo(() => {
    const timeSeries = financialData?.report?.data?.timeSeries;
    console.log('📊 Chart Data Debug:', {
      hasFinancialData: !!financialData,
      hasReport: !!financialData?.report,
      hasData: !!financialData?.report?.data,
      hasTimeSeries: !!timeSeries,
      timeSeriesLength: timeSeries?.length || 0,
      timeSeries: timeSeries
    });
    
    if (!timeSeries || timeSeries.length === 0) {
      console.log('📊 No timeSeries data generated - returning empty chart');
      const currentDate = new Date();
      const emptyData = [];
      
      // Create empty months for structure
      for (let i = 5; i >= 0; i--) {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        const monthStr = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
        
        emptyData.push({
          month: monthStr,
          revenue: 0,
          expenses: 0,
          profit: 0
        });
      }
      
      return emptyData;
    }
    
    // Check if we have ANY revenue at all
    const hasRevenue = timeSeries.some(item => item.income > 0);
    console.log('📊 Chart Data Analysis:', {
      timeSeriesLength: timeSeries.length,
      hasRevenue,
      totalRevenue: timeSeries.reduce((sum, item) => sum + item.income, 0),
      sampleMonth: timeSeries[timeSeries.length - 1] // Current month
    });
    
    if (!hasRevenue) {
      console.log('📊 No revenue found in timeSeries - but keeping structure for potential future data');
    }
    
    return timeSeries.map(item => ({
    month: item.period,
      revenue: item.income || 0,
      expenses: item.expenses || 0,
      profit: item.profit || 0
    }));
  }, [financialData]);

  const studentsChartData = studentsData?.monthlyData || [];
  const coursesPieData = coursesData?.distribution || [];
  const salesBarData = salesData?.monthlySales || [];

  // Traitement des données en fonction du type de rapport
  let reportData = [];
  let reportTitle = '';
  let reportDescription = '';
  let chartComponent = null;

  switch (reportType) {
    case 'financial':
      reportData = financialChartData;
      reportTitle = 'Rapport Financier';
      reportDescription = 'Aperçu des revenus, dépenses et bénéfices';
      
      console.log('📊 Financial Chart Rendering:', {
        reportDataLength: reportData?.length || 0,
        reportData: reportData,
        sampleItem: reportData?.[0]
      });
      
      chartComponent = (
        <div className="h-96">
          {isFinancialLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              <span className="ml-3 text-slate-900 dark:text-slate-50">Chargement des données financières...</span>
            </div>
          ) : viewMode === 'table' ? (
            renderFinancialTable(reportData)
          ) : reportData && reportData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={reportData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={formatCurrency} />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#94a3b8' }} />
              <Bar dataKey="revenue" name="Revenus" fill="#3b82f6" />
              <Bar dataKey="expenses" name="Dépenses" fill="#ef4444" />
              <Bar dataKey="profit" name="Bénéfices" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-600 dark:text-slate-400">
                <p className="text-lg mb-2">📊 Aucune donnée de revenus</p>
                <p className="text-sm mb-2">Allez à la page <strong>Paiements</strong> pour ajouter des transactions</p>
                <p className="text-xs">Les données apparaîtront automatiquement ici une fois les paiements ajoutés</p>
              </div>
            </div>
          )}
        </div>
      );
      break;

    case 'students':
      reportData = studentsChartData;
      reportTitle = 'Rapport des Étudiants';
      reportDescription = 'Statistiques sur l\'évolution du nombre d\'étudiants';
      chartComponent = (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={reportData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#94a3b8' }} />
              <Line type="monotone" dataKey="nouveaux" name="Nouveaux" stroke="#3b82f6" activeDot={{ r: 8 }} />
              <Line type="monotone" dataKey="actifs" name="Actifs" stroke="#10b981" />
              <Line type="monotone" dataKey="inactifs" name="Inactifs" stroke="#ef4444" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
      break;

    case 'courses':
      reportData = coursesPieData;
      reportTitle = 'Répartition des Cours';
      reportDescription = 'Distribution des étudiants par cours';
      
      console.log('📊 Courses Pie Chart Data:', reportData);
      
      chartComponent = (
        <div className="h-96">
          {reportData && reportData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={reportData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {reportData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value} étudiant${value > 1 ? 's' : ''}`,
                    props.payload.revenue ? `Revenus: ${props.payload.revenue.toLocaleString()} DA` : ''
                  ]}
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  itemStyle={{ color: '#f8fafc' }}
                  labelStyle={{ color: '#94a3b8' }}
                />
                <Legend wrapperStyle={{ color: '#94a3b8' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-600 dark:text-slate-400">
                <p className="text-lg mb-2">📚 Aucun cours disponible</p>
                <p className="text-sm">Créez des cours dans la section Cours pour voir la répartition</p>
              </div>
            </div>
          )}
        </div>
      );
      break;

    case 'sales':
      reportData = salesBarData;
      reportTitle = 'Ventes Mensuelles';
      reportDescription = 'Évolution des ventes par mois';
      chartComponent = (
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={reportData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" tickFormatter={(value) => `${value.toLocaleString()} DA`} />
              <Tooltip 
                formatter={(value) => `${value.toLocaleString()} DA`}
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                itemStyle={{ color: '#f8fafc' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#94a3b8' }} />
              <Bar dataKey="sales" name="Ventes" fill="#3b82f6" />
              <Bar dataKey="transactions" name="Transactions" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
      break;
    default:
      reportData = financialChartData;
  }

  // Options de période
  const dateRangeOptions = [
    { id: 'week', name: 'Cette semaine' },
    { id: 'month', name: 'Ce mois' },
    { id: 'quarter', name: 'Ce trimestre' },
    { id: 'year', name: 'Cette année' },
    { id: 'custom', name: 'Personnalisé' },
  ];

  // Types de rapports
  const reportTypes = [
    { id: 'financial', name: 'Finances', icon: BanknotesIcon },
    { id: 'students', name: 'Étudiants', icon: AcademicCapIcon },
    { id: 'courses', name: 'Cours', icon: ClipboardDocumentCheckIcon },
    { id: 'sales', name: 'Sales', icon: PresentationChartBarIcon },
  ];

  const handleExportReport = async () => {
    setIsExporting(true);
    console.log(`📄 Exporting real-time ${reportType} report for period ${dateRange}...`);
    
    // Debug: Check data availability before export
    console.log('📊 Export Debug - Data availability:', {
      reportType,
      organizationSettings: organizationSettings ? '✅ Available' : '❌ Missing',
      financialData: financialData ? '✅ Available' : '❌ Missing',
      studentsData: studentsData ? '✅ Available' : '❌ Missing', 
      coursesData: coursesData ? '✅ Available' : '❌ Missing',
      salesData: salesData ? '✅ Available' : '❌ Missing',
      isFinancialLoading,
      isStudentsLoading,
      isCoursesLoading,
      isSalesLoading
    });
    
    // Check if data is still loading
    if (isFinancialLoading || isStudentsLoading || isCoursesLoading || isSalesLoading) {
      toast.error('⏳ Veuillez attendre que les données se chargent avant d\'exporter');
      setIsExporting(false);
      return;
    }
    
    try {
      // Create new PDF document
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 20;
      let yPosition = margin;

      // Helper function to get period display text
      const getPeriodDisplayText = () => {
        const options = {
          week: 'Cette semaine',
          month: 'Ce mois',
          quarter: 'Ce trimestre', 
          year: 'Cette année',
          custom: `${customStartDate} au ${customEndDate}`
        };
        return options[dateRange] || 'Période personnalisée';
      };

      // Add professional header with school branding
      const addHeader = () => {
        // Add school logo if available
        if (organizationSettings?.logo) {
          try {
            pdf.addImage(organizationSettings.logo, 'PNG', margin, yPosition, 30, 30);
          } catch (error) {
            console.warn('Could not add logo to PDF:', error);
          }
        }

        // School name and title
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.text(organizationSettings?.gymName || 'FORMAPRO', organizationSettings?.logo ? margin + 40 : margin, yPosition + 10);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        pdf.text(organizationSettings?.address || 'École de Formation', organizationSettings?.logo ? margin + 40 : margin, yPosition + 18);
        
        if (organizationSettings?.phone) {
          pdf.text(`Tél: ${organizationSettings.phone}`, organizationSettings?.logo ? margin + 40 : margin, yPosition + 25);
        }
        
        // Report title and info on the right
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        const reportTitle = `Rapport ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`;
        pdf.text(reportTitle, pageWidth - margin, yPosition + 10, { align: 'right' });
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const periodText = getPeriodDisplayText();
        pdf.text(`Période: ${periodText}`, pageWidth - margin, yPosition + 18, { align: 'right' });
        
        const currentDate = new Date().toLocaleDateString('fr-FR');
        pdf.text(`Généré le: ${currentDate}`, pageWidth - margin, yPosition + 25, { align: 'right' });
        
        // Add horizontal line
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPosition + 35, pageWidth - margin, yPosition + 35);
        
        return yPosition + 45;
      };

      yPosition = addHeader();

      // Add report content based on type
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      
      switch (reportType) {
        case 'financial':
          console.log('📄 Adding financial report section...');
          addFinancialReport(pdf, yPosition, margin);
          break;
        case 'students':
          console.log('📄 Adding students report section...');
          addStudentsReport(pdf, yPosition, margin);
          break;
        case 'courses':
          console.log('📄 Adding courses report section...');
          addCoursesReport(pdf, yPosition, margin);
          break;
        case 'sales':
          console.log('📄 Adding sales report section...');
          addSalesReport(pdf, yPosition, margin);
          break;
      }

      // Add footer
      const addFooter = () => {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(
          `Document généré automatiquement par ${organizationSettings?.gymName || 'FORMAPRO'} - ${new Date().toLocaleString('fr-FR')}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      };

      addFooter();

      // Save PDF
      const fileName = `rapport-${reportType}-${dateRange}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success(`✅ Rapport PDF exporté avec succès: ${fileName}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('❌ Erreur lors de l\'exportation du rapport PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Helper functions for different report types
  const addFinancialReport = (pdf, startY, margin) => {
    console.log('📊 Financial Report Data Check:', {
      financialData: !!financialData,
      reportData: !!financialData?.report,
      dataSection: !!financialData?.report?.data,
      summary: !!financialData?.report?.data?.summary,
      timeSeries: financialData?.report?.data?.timeSeries?.length || 0
    });
    
    let yPos = startY;
    const summary = financialData?.report?.data?.summary;
    const timeSeries = financialData?.report?.data?.timeSeries || [];

    // Summary section
    pdf.text('Résumé Financier', margin, yPos);
    yPos += 10;

    if (summary) {
      const summaryData = [
        ['Revenus totaux', `${(summary.totalIncome || 0).toLocaleString()} DA`],
        ['Dépenses totales', `${(summary.totalExpenses || 0).toLocaleString()} DA`],
        ['Bénéfice net', `${(summary.netProfit || 0).toLocaleString()} DA`],
        ['Marge bénéficiaire', `${(summary.profitMargin || 0).toFixed(1)}%`]
      ];

      autoTable(pdf, {
        startY: yPos,
        head: [['Indicateur', 'Valeur']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [68, 84, 195] },
        margin: { left: margin, right: margin }
      });

      yPos = pdf.lastAutoTable?.finalY ? pdf.lastAutoTable.finalY + 15 : yPos + 50;
    } else {
      pdf.text('Aucune donnée financière disponible', margin, yPos);
      yPos += 15;
    }

    // Time series data
    if (timeSeries.length > 0) {
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Évolution Temporelle', margin, yPos);
      yPos += 10;

      const timeSeriesData = timeSeries.map(item => [
        item.period || 'N/A',
        `${(item.income || 0).toLocaleString()} DA`,
        `${(item.expenses || 0).toLocaleString()} DA`,
        `${(item.profit || 0).toLocaleString()} DA`
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: [['Période', 'Revenus', 'Dépenses', 'Bénéfice']],
        body: timeSeriesData,
        theme: 'striped',
        headStyles: { fillColor: [34, 197, 94] },
        margin: { left: margin, right: margin }
      });
    }
  };

  const addStudentsReport = (pdf, startY, margin) => {
    console.log('👥 Students Report Data Check:', {
      studentsData: !!studentsData,
      summary: !!studentsData?.summary
    });
    
    let yPos = startY;
    const summary = studentsData?.summary;

    pdf.text('Rapport Étudiants', margin, yPos);
    yPos += 10;

    if (summary) {
      const studentsTableData = [
        ['Nouveaux étudiants', (summary.newThisMonth || 0).toString()],
        ['Étudiants actifs', (summary.activeStudents || 0).toString()],
        ['Étudiants inactifs', (summary.inactiveStudents || 0).toString()],
        ['Total étudiants', (summary.totalStudents || 0).toString()]
      ];

      autoTable(pdf, {
        startY: yPos,
        head: [['Indicateur', 'Nombre']],
        body: studentsTableData,
        theme: 'grid',
        headStyles: { fillColor: [147, 51, 234] },
        margin: { left: margin, right: margin }
      });
    } else {
      pdf.text('Aucune donnée étudiante disponible', margin, yPos);
    }
  };

  const addCoursesReport = (pdf, startY, margin) => {
    console.log('📚 Courses Report Data Check:', {
      coursesData: !!coursesData,
      courseAnalytics: !!coursesData?.courseAnalytics,
      analyticsCount: Object.keys(coursesData?.courseAnalytics || {}).length
    });
    
    let yPos = startY;
    const courseAnalytics = coursesData?.courseAnalytics || {};

    pdf.text('Rapport Cours', margin, yPos);
    yPos += 10;

    const coursesTableData = Object.values(courseAnalytics).map(course => [
      course.name || 'N/A',
      (course.enrolledStudents || 0).toString(),
      `${(course.revenue || 0).toLocaleString()} DA`,
      `${(course.completionRate || 0).toFixed(1)}%`
    ]);

    if (coursesTableData.length > 0) {
      autoTable(pdf, {
        startY: yPos,
        head: [['Cours', 'Étudiants', 'Revenus', 'Taux de Réussite']],
        body: coursesTableData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: margin, right: margin }
      });
    } else {
      pdf.text('Aucune donnée de cours disponible', margin, yPos);
    }
  };

  const addSalesReport = (pdf, startY, margin) => {
    console.log('💰 Sales Report Data Check:', {
      salesData: !!salesData,
      salesByMethod: !!salesData?.salesByMethod,
      methodsCount: Object.keys(salesData?.salesByMethod || {}).length
    });
    
    let yPos = startY;
    const salesByMethod = salesData?.salesByMethod || {};

    pdf.text('Rapport Ventes', margin, yPos);
    yPos += 10;

    const salesTableData = Object.entries(salesByMethod).map(([method, amount]) => [
      method,
      `${(amount || 0).toLocaleString()} DA`
    ]);

    if (salesTableData.length > 0) {
      autoTable(pdf, {
        startY: yPos,
        head: [['Méthode de Paiement', 'Montant']],
        body: salesTableData,
        theme: 'grid',
        headStyles: { fillColor: [245, 101, 101] },
        margin: { left: margin, right: margin }
      });
    } else {
      pdf.text('Aucune donnée de ventes disponible', margin, yPos);
    }
  };

  // Listen for localStorage changes to auto-refresh reports
  React.useEffect(() => {
    const handleStorageChange = (event) => {
      const importantKeys = ['payments', 'students', 'courses', 'expenses', 'trainers', 'sessions'];
      
      if (importantKeys.includes(event.key)) {
        console.log('📊 Reports: Data changed in localStorage, auto-refreshing...', event.key);
        
        // Immediate refresh for real-time data changes
        if (event.key === 'payments') {
          console.log('💰 PAYMENTS DATA CHANGED - refreshing financial charts for real-time updates');
          setTimeout(() => {
            refetchFinancial();
            refetchSales();
            refetchDashboard();
          }, 50); // Quick refresh for immediate chart updates
        } else if (event.key === 'students') {
          refetchStudents();
          refetchDashboard();
        } else if (event.key === 'courses') {
          refetchCourses();
          refetchDashboard();
        } else if (event.key === 'expenses') {
          refetchFinancial();
          refetchDashboard();
        } else {
          // For other keys, refresh all
          handleRefresh();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refetchFinancial, refetchStudents, refetchCourses, refetchSales, refetchDashboard]);


  const handleRefresh = () => {
    setIsExporting(true);
    console.log('📊 Reports: FORCE REFRESHING all real-time data...');
    
    // Clear all caches first
    queryClient.clear();
    
    Promise.all([
      refetchFinancial(),
      refetchStudents(),
      refetchCourses(),
      refetchSales(),
      refetchDashboard()
    ]).then(() => {
      toast.success('Données actualisées avec succès!');
      console.log('📊 Reports: All real-time data refreshed successfully');
    }).catch((error) => {
      console.error('Error refreshing reports:', error);
      toast.error('Erreur lors de l\'actualisation des données');
    }).finally(() => {
      setIsExporting(false);
    });
  };

  const formaLinkBtn = FORMA_LINK_BTN;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <PageHeader
        title="Rapports"
        description="Visualisez et analysez les données de l'école"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isExporting}
              className={clsx(
                'rounded-xl border border-slate-200 bg-white p-2 shadow-sm transition dark:border-slate-600 dark:bg-slate-900',
                isExporting
                  ? 'cursor-not-allowed text-slate-400 opacity-60'
                  : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
              )}
              title="Rafraîchir les données"
            >
              <ArrowPathIcon className={clsx('h-5 w-5', isExporting && 'animate-spin')} />
            </button>
            <button
              type="button"
              onClick={handleExportReport}
              disabled={isExporting}
              className={clsx(formaLinkBtn, 'disabled:cursor-not-allowed disabled:opacity-50')}
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Exporter</span>
            </button>
          </div>
        }
      />

      {/* Cartes statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Revenus totaux</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.monthlyRevenue ? 
                  `${dashboardData.data.monthlyRevenue.toLocaleString()} DA` : 
                  '0 DA'
                }
              </p>
            </div>
            <div className="p-3 rounded-lg bg-forma-500/15">
              <BanknotesIcon className="h-6 w-6 text-forma-700 dark:text-forma-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-green-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
            <span>Données temps réel</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Étudiants actifs</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.activeStudents || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-teal-500/15">
              <AcademicCapIcon className="h-6 w-6 text-teal-700 dark:text-teal-300" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-green-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
            <span>Étudiants actifs seulement</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Cours actifs</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.activeCourses || 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-green-500/20">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-yellow-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l1 1a1 1 0 01-1.414 1.414L10 5.414 8.707 6.707a1 1 0 01-1.414-1.414l1-1A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span>Cours disponibles</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl p-6 border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600 dark:text-slate-400 text-sm">Taux d'occupation</p>
              <p className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mt-2">
                {dashboardData?.data?.totalStudents > 0 ? 
                  `${Math.round((dashboardData.data.activeStudents / dashboardData.data.totalStudents) * 100)}%` : 
                  '0%'
                }
              </p>
            </div>
            <div className="p-3 rounded-lg bg-orange-500/20">
              <BuildingLibraryIcon className="h-6 w-6 text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-green-400 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
            </svg>
            <span>Taux d'occupation calculé</span>
          </div>
        </motion.div>
      </div>

      {/* Filtres et sélection de rapport */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-1">
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 backdrop-blur-xl border border-slate-200 dark:border-slate-600 h-full">
            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Type de rapport</h2>
            <div className="space-y-2">
              {reportTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => setReportType(type.id)}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                    reportType === type.id
                      ? 'border border-forma-200/80 bg-forma-50/80 dark:border-forma-900/50 dark:bg-forma-950/30'
                      : 'hover:bg-slate-100/80 dark:bg-slate-800/40'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${reportType === type.id ? 'bg-forma-500/15' : 'bg-slate-100/80 dark:bg-slate-800/40'}`}>
                    <type.icon className={`h-5 w-5 ${reportType === type.id ? 'text-forma-700 dark:text-forma-300' : 'text-slate-600 dark:text-slate-400'}`} />
                  </div>
                  <span className={`text-sm font-medium ${reportType === type.id ? 'text-slate-900 dark:text-slate-50' : 'text-slate-600 dark:text-slate-400'}`}>
                    {type.name}
                  </span>
                </button>
              ))}
            </div>

            <h2 className="text-lg font-medium text-slate-900 dark:text-slate-50 mt-6 mb-4">Période</h2>
            <div className="space-y-2">
              {dateRangeOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setDateRange(option.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg text-sm transition-colors ${
                    dateRange === option.id
                      ? 'bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/80 dark:bg-slate-800/40 hover:text-slate-900 dark:text-slate-50'
                  }`}
                >
                  <span>{option.name}</span>
                  {dateRange === option.id && (
                    <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                  )}
                </button>
              ))}
            </div>

            {dateRange === 'custom' && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Date de début</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2 text-slate-900 dark:text-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-forma-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Date de fin</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2 text-slate-900 dark:text-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-forma-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 backdrop-blur-xl border border-slate-200 dark:border-slate-600">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">{reportTitle}</h2>
                <p className="text-slate-600 dark:text-slate-400 text-sm mt-1">{reportDescription}</p>
              </div>
              {reportType === 'financial' && (
                <div className="mt-4 sm:mt-0 flex items-center">
                  <div className="flex items-center bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-1">
                    <button 
                      onClick={() => setViewMode('chart')}
                      className={`px-3 py-1 text-sm rounded-md transition-all duration-200 ${
                        viewMode === 'chart' 
                          ? 'bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 font-medium' 
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100/80 dark:bg-slate-800/40'
                      }`}
                    >
                      Graphique
                    </button>
                    <button 
                      onClick={() => setViewMode('table')}
                      className={`px-3 py-1 text-sm rounded-md transition-all duration-200 ${
                        viewMode === 'table' 
                          ? 'bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 font-medium' 
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100/80 dark:bg-slate-800/40'
                      }`}
                    >
                      Tableau
                    </button>
                  </div>
                </div>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white/30"></div>
              </div>
            ) : (
              chartComponent
            )}
          </div>
        </div>
      </div>

    </motion.div>
  );
};

export default ReportsPage;